import { recipePhotoUrl } from '@awwyeah/shared';
import type { AttemptOutcome } from '../../../lib/outbox/queue';
import { RetryQueue } from '../../../lib/outbox/queue';
import type { OutboxStore, RecipePhotoOutboxRecord } from './store';

/**
 * The recipe photo outbox (WS-20) — same shape as check-in's `CheckinOutbox`, minus the
 * announcement queue (a recipe photo has nothing that needs a push notification). Records
 * are keyed by the photo's own content-addressed id (same as check-in), so "change photo"
 * while the previous upload is still queued leaves both queued rather than one replacing
 * the other — harmless (both are valid, immutable, content-addressed blobs; the recipe's
 * `photoRef` in Yjs is the only thing that decides which one is "current", and the UI
 * swaps that the instant the *new* upload finishes queuing, not when it finishes
 * uploading — see `RecipeDetailPage`'s upload handler). The old blob simply goes unused
 * once uploaded, which is the documented v1 trade-off (DECISIONS 2026-08-04).
 */

export interface RecipePhotoOutboxOptions {
  photos: OutboxStore<RecipePhotoOutboxRecord>;
  /** Called when a photo's bytes are safely on the server — flips Yjs `photoRef.state`. */
  onPhotoUploaded: (record: RecipePhotoOutboxRecord) => void;
  onPhotoUploading?: (record: RecipePhotoOutboxRecord) => void;
  fetchImpl?: typeof fetch;
  now?: () => number;
  onChange?: () => void;
  baseDelayMs?: number;
  maxDelayMs?: number;
  setTimeoutImpl?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeoutImpl?: (handle: ReturnType<typeof setTimeout>) => void;
}

/** Same rule as check-in's outbox: the server has looked at these exact bytes and said no
 * (400/413/415) ⇒ drop, retrying is guaranteed to fail identically. Everything else,
 * including 401 and 5xx, is transient. */
export function outcomeForStatus(status: number): AttemptOutcome {
  if (status >= 200 && status < 300) return 'done';
  if (status === 400 || status === 413 || status === 415) return 'drop';
  return 'retry';
}

export class RecipePhotoOutbox {
  private readonly queue: RetryQueue<RecipePhotoOutboxRecord>;
  private readonly fetchImpl: typeof fetch;
  private onlineListener: (() => void) | null = null;

  constructor(private readonly options: RecipePhotoOutboxOptions) {
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));

    this.queue = new RetryQueue<RecipePhotoOutboxRecord>({
      store: options.photos,
      attempt: (record) => this.upload(record),
      ...(options.now ? { now: options.now } : {}),
      ...(options.onChange ? { onChange: options.onChange } : {}),
      ...(options.baseDelayMs !== undefined ? { baseDelayMs: options.baseDelayMs } : {}),
      ...(options.maxDelayMs !== undefined ? { maxDelayMs: options.maxDelayMs } : {}),
      ...(options.setTimeoutImpl ? { setTimeoutImpl: options.setTimeoutImpl } : {}),
      ...(options.clearTimeoutImpl ? { clearTimeoutImpl: options.clearTimeoutImpl } : {}),
    });
  }

  start(): void {
    this.queue.start();
  }

  stop(): void {
    this.queue.stop();
    if (this.onlineListener && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
  }

  listenForOnline(): void {
    if (typeof window === 'undefined' || this.onlineListener) return;
    this.onlineListener = (): void => void this.flush();
    window.addEventListener('online', this.onlineListener);
  }

  async flush(): Promise<void> {
    await this.queue.drain();
  }

  /** Replaces any previously-queued photo for this recipe — "change photo" while the first
   * upload is still in flight just supersedes it. */
  async queuePhoto(
    record: Omit<RecipePhotoOutboxRecord, 'kind' | 'attempts' | 'nextAttemptAt' | 'createdAt'>,
  ): Promise<void> {
    await this.queue.enqueue({ ...record, kind: 'recipe-photo', createdAt: Date.now() });
  }

  async pending(): Promise<RecipePhotoOutboxRecord[]> {
    return this.queue.pending();
  }

  private async upload(record: RecipePhotoOutboxRecord): Promise<AttemptOutcome> {
    this.options.onPhotoUploading?.(record);

    const response = await this.fetchImpl(recipePhotoUrl(record.id), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': record.contentType },
      body: record.blob,
    });

    const outcome = outcomeForStatus(response.status);
    if (outcome === 'done') this.options.onPhotoUploaded(record);
    if (outcome === 'drop') {
      console.error(
        `[recipes] server refused photo ${record.id.slice(0, 12)}… (${response.status}); ` +
          'it stays on this device only',
      );
    }
    return outcome;
  }
}
